import { Box, Button } from '@mui/material';
import {
    ChangeEvent,
    MouseEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { getSelectedCatalogItem } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { fetchCategories, getLocalCategoriesList } from 'entities/Category';
import {
    fetchCustomListDetail,
    getCustomListCatalogItem,
    getCustomListCatalogItemId,
    getCustomListDetail,
    getCustomListDetailLoading,
} from 'entities/CustomList';
import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import { getUserCanManageCustomLists } from 'entities/User/model/selectors/userPermissionsSeletors';
import { RemoveCustomListItemsMode } from 'features/RemoveCustomListItems';
import {
    getRouteCatalogListAddItemsCategories,
    getRouteCatalogListItem,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { EmptyResults } from 'shared/ui/EmptyResults/EmptyResults';
import { Loader } from 'shared/ui/Loader/Loader';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog';

import { CustomListDetailDialogs } from './CustomListDetailDialogs';
import { CustomListDetailFilters } from './CustomListDetailFilters';
import { CustomListDetailHeader } from './CustomListDetailHeader';
import { CustomListDetailItemsTable } from './CustomListDetailItemsTable';

const detailShellSx = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
} as const;

export const CustomListDetail = memo(() => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { propertyId = '', listId = '', itemId } = useParams();

    const detail = useSelector(getCustomListDetail);
    const isLoading = useSelector(getCustomListDetailLoading);
    const canManage = useSelector(getUserCanManageCustomLists);
    const categories = useSelector(getLocalCategoriesList);
    const catalogId = useSelector(getSelectedPropertyCatalogId);
    const selectedItem = useSelector(getSelectedCatalogItem);

    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [showRename, setShowRename] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [removeMode, setRemoveMode] =
        useState<RemoveCustomListItemsMode>('fromList');
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [pendingListItemIds, setPendingListItemIds] = useState<string[]>([]);
    const [pendingCatalogItemIds, setPendingCatalogItemIds] = useState<
        string[]
    >([]);

    useEffect(() => {
        if (listId) {
            dispatch(fetchCustomListDetail({ listId }));
        }
    }, [dispatch, listId]);

    useEffect(() => {
        if (catalogId) {
            dispatch(fetchCategories(catalogId));
        }
    }, [catalogId, dispatch]);

    useEffect(() => {
        setSelectedIds([]);
        setSearch('');
        setCategoryFilter('all');
    }, [listId]);

    const routeDetail = detail?.id === listId ? detail : null;
    const items = useMemo(() => routeDetail?.items ?? [], [routeDetail]);

    // Deep-link / refresh: open item detail from route once list items load.
    useEffect(() => {
        if (!itemId || items.length === 0) {
            return;
        }

        const listItem = items.find(
            (entry) => getCustomListCatalogItemId(entry) === itemId,
        );

        const catalogItem = listItem
            ? getCustomListCatalogItem(listItem)
            : undefined;

        if (catalogItem) {
            dispatch(catalogItemActions.setSelectedItem(catalogItem));
            dispatch(catalogItemActions.setShowDetailedView(true));
        }
    }, [dispatch, itemId, items]);

    useEffect(() => {
        if (!itemId) {
            dispatch(catalogItemActions.closeDetailedItem());
        }
    }, [dispatch, itemId]);

    const resolvedCategoryFilter = useMemo(() => {
        if (categoryFilter === 'all') {
            return categoryFilter;
        }

        const categoryStillHasItems = items.some(
            (listItem) =>
                getCustomListCatalogItem(listItem)?.categoryId ===
                categoryFilter,
        );

        return categoryStillHasItems ? categoryFilter : 'all';
    }, [categoryFilter, items]);

    const filteredItems = useMemo(() => {
        const query = search.trim().toLowerCase();
        return items.filter((listItem) => {
            const catalogItem = getCustomListCatalogItem(listItem);
            const matchesSearch =
                !query ||
                catalogItem?.title?.toLowerCase().includes(query) ||
                catalogItem?.sku?.toLowerCase().includes(query);
            const matchesCategory =
                resolvedCategoryFilter === 'all' ||
                catalogItem?.categoryId === resolvedCategoryFilter;
            return matchesSearch && matchesCategory;
        });
    }, [items, resolvedCategoryFilter, search]);

    const categoryOptions = useMemo(() => {
        const ids = new Set(
            items
                .map(
                    (listItem) =>
                        getCustomListCatalogItem(listItem)?.categoryId,
                )
                .filter(Boolean),
        );
        return Object.values(categories || {}).filter((cat) => ids.has(cat.id));
    }, [categories, items]);

    const itemDetailedOutletContext = useMemo(
        () => ({
            isDetailed: true,
            item: selectedItem,
            moveTo: PropertyCatalogItemsMoveTo.CUSTOM_LIST,
        }),
        [selectedItem],
    );

    const onToggleAll = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedIds(filteredItems.map((item) => item.id));
        } else {
            setSelectedIds([]);
        }
    };

    const onToggleRow = (itemIdToToggle: string) => {
        setSelectedIds((prev) =>
            prev.includes(itemIdToToggle)
                ? prev.filter((id) => id !== itemIdToToggle)
                : [...prev, itemIdToToggle],
        );
    };

    const openRemoveMenu = (
        event: MouseEvent<HTMLElement>,
        listItemIds: string[],
    ) => {
        event.stopPropagation();
        const selectedListItems = items.filter((item) =>
            listItemIds.includes(item.id),
        );
        setPendingListItemIds(listItemIds);
        setPendingCatalogItemIds(
            selectedListItems
                .map((listItem) => getCustomListCatalogItemId(listItem))
                .filter(Boolean) as string[],
        );
        setMenuAnchor(event.currentTarget);
    };

    const onSelectRemoveMode = (mode: RemoveCustomListItemsMode) => {
        setRemoveMode(mode);
        setShowRemoveDialog(true);
    };

    const goAddItems = () => {
        navigate(getRouteCatalogListAddItemsCategories(propertyId, listId));
    };

    const onExpandItem = useCallback(
        (catalogItem: CatalogItem) => {
            const routeItemId = catalogItem.catalogItemId || catalogItem.id;
            dispatch(catalogItemActions.setSelectedItem(catalogItem));
            dispatch(catalogItemActions.setShowDetailedView(true));
            navigate(getRouteCatalogListItem(propertyId, listId, routeItemId));
        },
        [dispatch, listId, navigate, propertyId],
    );

    if (!routeDetail) {
        if (isLoading || detail) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '48px',
                    }}
                >
                    <Loader />
                </Box>
            );
        }

        return (
            <EmptyResults
                title={t('customLists.listNotFound')}
                dataTestClass="custom-list-not-found"
            />
        );
    }

    const isEmpty = items.length === 0;
    const showItemDetail = Boolean(itemId);

    const header = (
        <CustomListDetailHeader
            listName={routeDetail.name}
            canManage={canManage}
            onDelete={() => setShowDelete(true)}
            onRename={() => setShowRename(true)}
            onAddItems={goAddItems}
        />
    );

    const dialogs = (
        <CustomListDetailDialogs
            propertyId={propertyId}
            listId={routeDetail.id}
            listName={routeDetail.name}
            showRename={showRename}
            showDelete={showDelete}
            showRemoveDialog={showRemoveDialog}
            removeMode={removeMode}
            menuAnchor={menuAnchor}
            pendingListItemIds={pendingListItemIds}
            pendingCatalogItemIds={pendingCatalogItemIds}
            onCloseRename={() => setShowRename(false)}
            onCloseDelete={() => setShowDelete(false)}
            onCloseMenu={() => setMenuAnchor(null)}
            onSelectRemoveMode={onSelectRemoveMode}
            onCloseRemoveDialog={() => setShowRemoveDialog(false)}
            onRemoveSuccess={() => setSelectedIds([])}
        />
    );

    if (isEmpty) {
        return (
            <Box data-testclass="custom-list-detail" sx={detailShellSx}>
                {header}
                <EmptyResults
                    title={t('customLists.noItemsTitle')}
                    description={t('customLists.emptyDescription')}
                    dataTestClass="custom-list-empty"
                    action={
                        canManage ? (
                            <Button
                                variant="contained"
                                onClick={goAddItems}
                                data-testclass="custom-list-empty-add-items"
                            >
                                {t('customLists.addItemsToListCta')}
                            </Button>
                        ) : undefined
                    }
                />
                {dialogs}
            </Box>
        );
    }

    if (showItemDetail) {
        return (
            <Box data-testclass="custom-list-detail" sx={detailShellSx}>
                <Box
                    sx={{
                        padding: '16px',
                        borderRadius: '8px',
                        bgcolor: 'background.paper',
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                    }}
                >
                    <Outlet context={itemDetailedOutletContext} />
                </Box>
                {dialogs}
            </Box>
        );
    }

    return (
        <Box data-testclass="custom-list-detail" sx={detailShellSx}>
            {header}
            <CustomListDetailFilters
                search={search}
                setSearch={setSearch}
                categoryFilter={resolvedCategoryFilter}
                setCategoryFilter={setCategoryFilter}
                categoryOptions={categoryOptions}
            />
            <CustomListDetailItemsTable
                listId={listId}
                items={filteredItems}
                selectedIds={selectedIds}
                canManage={canManage}
                onToggleAll={onToggleAll}
                onToggleRow={onToggleRow}
                onOpenRemoveMenu={openRemoveMenu}
                onExpandItem={onExpandItem}
            />
            {dialogs}
        </Box>
    );
});
